const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8787/api';

export interface LoginResponse {
  token: string;
  user: {
    id: string;
    username: string;
    name: string;
    email: string;
    role: string;
    vendorId?: string | null;
    permissions: string[];
  };
}

class AuthService {
  async login(username: string, password: string): Promise<LoginResponse> {
    const response = await fetch(`${API_BASE_URL}/users/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password }),
    });

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.message || 'Login failed');
    }

    localStorage.setItem('oni_token', data.token);
    return data as LoginResponse;
  }

  async logout(): Promise<void> {
    this.clearTokens();
  }

  getToken(): string | null {
    return localStorage.getItem('oni_token');
  }

  private setTokens(token: string): void {
    localStorage.setItem('oni_token', token);
  }

  /**
   * Clear all tokens
   */
  clearTokens(): void {
    localStorage.removeItem('oni_token');
    localStorage.removeItem('oni_refresh_token');
    localStorage.removeItem('oni_current_user');
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return !!this.getToken();
  }

  /**
   * Decode JWT token (client-side parsing only, not validation)
   */
  decodeToken(token: string): unknown {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('Failed to decode token:', error);
      return null;
    }
  }

  /**
   * Check if token is expired
   */
  isTokenExpired(token: string): boolean {
    const decoded = this.decodeToken(token) as { exp?: number } | null;
    if (!decoded || !decoded.exp) {
      return true;
    }
    
    // Check if token expires in the next 5 minutes
    const expirationTime = decoded.exp * 1000;
    const now = Date.now();
    const buffer = 5 * 60 * 1000; // 5 minutes
    
    return expirationTime - now < buffer;
  }
}

export const authService = new AuthService();
export default authService;
